import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { Group, Badge, Tooltip } from '@mantine/core';
import type { CSSObject } from '@mantine/core';
import { textVariants } from '@ui/tokens/typography/typography';
import Button from '../../new_arch/components/Button/Button';
import IconButton from '../../components/UI/IconButton/IconButton';
import Checkbox from '../../components/UI/Checkbox/Checkbox';
import { PRIMARY_BASE } from '../../styles/colors';
import FeaturePaywall from '../../new_arch/modules/workspaces/components/FeaturePaywall/FeaturePaywall';
import PricingPlanFeature from '../../entities/pricing/pricingPlanFeature';

import './LabelsGroupsModal.scss';
import useLabelsGroupsModalPresenter from '../../presenters/LabelsGroupsModalPresenter/useLabelsGroupsModalPresenter';
import { type LabelGroup } from '../../presenters/LabelsGroupsModalPresenter/LabelsGroupsModalPresenter';

type LabelsGroupsModalProps = {
	projectID: number;
	onClose: () => void;
	onConfirm: () => void;
}

const BADGE_STYLES = {
	root: {
		border: 0
	},
	inner: {
		...( textVariants[ 'caption-semibold' ] as CSSObject )
	}
};

const LabelsGroupsModal = ( { projectID, onClose = () => {}, onConfirm } : LabelsGroupsModalProps ) => {
	const presenter = useLabelsGroupsModalPresenter( projectID );
	const { workspace } = presenter;
	const isFeatureAvailable = workspace?.supportsFeature( PricingPlanFeature.LABEL_GROUPS ) || false;

	useEffect( () => {
		presenter.onMount().then( () => presenter.setLoading( false ) );
	}, [] );

	const uncheckAllGroups = () => {
		presenter.uncheckAllGroups();
	};

	const onModalClosed = () => {
		onClose();
		uncheckAllGroups();
	}

	const onConfirmClicked = () => {
		presenter.addLabelsFromGroups();
		onConfirm();
		onModalClosed();
	}

	const { workspaceID } = presenter;

	const linkToManageGroups = () => (
		<a className="linkToSettings" href={`/user/workspaces/${workspaceID}/projects`}>workspace settings</a>
	);

	const renderActions = () => (
		<div className={classNames( 'actions', { 'no-groups': !presenter.thereAreGroups } )}>
			{presenter.thereAreGroups && !presenter.isLoading && (
				<Button
					className="cancel-button"
					type="text"
					onClick={onModalClosed}
				>
					Cancel
				</Button>
			)}
			<Button
				className="confirm-button"
				type="text"
				onClick={onConfirmClicked}
			>
				{( presenter.thereAreGroups && !presenter.isLoading ) ? 'Add' : 'Got it'}
			</Button>
		</div>
	);

	const renderLabelsGroups = () => (
		<div className="GroupsList">
			{presenter.getLabelsGroups.map( ( group : LabelGroup ) => {
				const isVerybusyDemo = group.name.toLowerCase().includes( 'verybusy demo' );
				const res = (
					<div className="GroupItem" key={group.id}>
						<Checkbox
							key={group.name}
							label={group.name}
							onChange={() => { presenter.toggleGroupCheck( { groupID: group.id } ) }}
							value={presenter.isGroupChecked( { groupID: group.id } )}
							checkedColor={PRIMARY_BASE}
							disabled={!isVerybusyDemo && !isFeatureAvailable}
						/>
					</div>
				)
				return res;
			} ) }
		</div>
	);

	const renderEmptyState = () => (
		<div className="emptyState">
			<p>There aren&apos;t any groups available.</p>
			{presenter.userCanManageLabelsGroups
				? <p>Create or modify from {linkToManageGroups()}</p>
				: <p>Only owners and members can create or modify from workspace settings</p>}
		</div>
	);

	const renderHeader = () => (
		<div
			className={classNames( 'modalHeader',
				{
					'no-groups': !presenter.thereAreGroups,
					'cannot-manage-groups': !presenter.userCanManageLabelsGroups
				}
			)}
		>
			<div className="title">
				<Group spacing={10}>
					<h4>Label Groups</h4>
					{!isFeatureAvailable && presenter.userCanManageLabelsGroups && workspace && (
						<FeaturePaywall
							feature={PricingPlanFeature.LABEL_GROUPS}
							workspace={workspace}
							stackOrder={200}
						>
							{( { planName, paywallMessage } ) => (
								<Tooltip
									label={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{paywallMessage}</div>}
									withArrow
									position="top"
									offset={4}
									withinPortal
									arrowPosition="center"
									zIndex={2000}
								>
									<Badge color="green" size="xs" variant="light" styles={BADGE_STYLES}>
										{planName}
									</Badge>
								</Tooltip>
							)}
						</FeaturePaywall>
					)}
				</Group>
				<IconButton size={24} onClick={onModalClosed} icon="close" color="black" />
			</div>
		</div>
	);

	const renderContent = () => {
		if ( presenter.isLoading ) return ( <div className="loading"><p>Loading...</p></div> );
		return presenter.thereAreGroups ? renderLabelsGroups() : renderEmptyState();
	}

	const renderFooter = () => (
		<div className="modalFooter">
			{presenter.userCanManageLabelsGroups
				? <p>You can create or modify from {linkToManageGroups()}</p>
				: <p>Only owners and members can create or modify</p>}
		</div>
	);

	return (
		<div
			className="LabelsGroupsModal"
			data-testid="labels-groups-modal"
		>
			{renderHeader()}
			{renderContent()}
			{renderActions()}
			{presenter.thereAreGroups && renderFooter()}
		</div>
	);
}

export default observer( LabelsGroupsModal );
